import { useState } from 'react';
import { Copy, Reply } from 'lucide-react';
import { colorForAgent } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { IconButton } from '../../ui';
import { AgentDot } from '../../../AgentDot';
import { agentShortForm } from '../../../../lib/use-agent-name';
import { type AgentSummary } from '../../../../lib/my-agents';
import type { Message } from '../../../../lib/use-chat';
import { initialsFromName, parseGroupAttribution, resolveGroupMentions } from '../../../../lib/wa-group-message';
import { useTranslation } from '../../../../lib/i18n';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { AgentJoinCard } from '../../AgentJoinCard';
import { CreditGiftCard } from '../../CreditGiftCard';
import { MilestoneCard } from '../../MilestoneCard';
import { MaestroMark } from '../../MaestroMark';
import { MeetingSummaryCard } from '../MeetingSummaryCard';
import { hexToRgba } from '../../../../lib/hex-to-rgba';
import { SentAttachmentList } from '../attachments';
import { LinkifiedText } from '../../../LinkifiedText';
import { ContactMentionText } from './ContactMentionText';
import { useTypewriter } from '../loaders';
import { formatMessageTime, voiceTranscript } from '../message-utils';
import { isAudioAttachment } from '../../../../lib/voice-message';
import { MicGlyph } from './MicGlyph';
import { StreamingCursor } from './StreamingCursor';
import { AssistantMarkdownContent } from './AssistantMarkdownContent';
import { ChoiceChips } from './ChoiceChips';
import { CreditsCard } from './CreditsCard';
import { ConnectorConnectCard } from './ConnectorConnectCard';
import { OpportunityCard } from './OpportunityCard';
import { QuickReactBar, ReactionChips, ReactToggleButton } from './MessageReactions';
import type { ChoiceOption } from '../../../../lib/message-choices';
import {
  AGENT_BUBBLE,
  ASSISTANT_BODY,
  CARD_WRAP,
  SPEAKER_AVATAR,
  SPEAKER_HEADER,
  SPEAKER_NAME,
  SPEAKER_TITLE,
  TIME_BASE,
  USER_BUBBLE,
  USER_BUBBLE_VOICE,
} from './styles';

// WhatsApp quoted-reply: a quiet quote block above the bubble body. `sender`/`preview` are
// resolved server-side (digest-engine); an unresolved reference renders the generic line.
function ReplyQuote({ replyTo }: { replyTo: NonNullable<Message['replyTo']> }) {
  const { t } = useTranslation();
  return (
    <div style={REPLY_QUOTE}>
      {replyTo.sender && <div style={REPLY_QUOTE_SENDER}>{replyTo.sender}</div>}
      <div dir="auto" style={REPLY_QUOTE_TEXT}>
        {replyTo.preview || t('chat.replyTo.generic')}
      </div>
    </div>
  );
}

const REPLY_QUOTE: React.CSSProperties = {
  borderInlineStart: '3px solid rgba(var(--ink-rgb),0.25)',
  padding: '4px 10px',
  marginBottom: 6,
  borderRadius: 6,
  background: 'rgba(var(--ink-rgb),0.05)',
  minWidth: 0,
  maxWidth: '100%',
  overflow: 'hidden',
};

const REPLY_QUOTE_SENDER: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  color: 'rgba(var(--ink-rgb),0.75)',
};

const REPLY_QUOTE_TEXT: React.CSSProperties = {
  fontSize: 12.5,
  color: 'rgba(var(--ink-rgb),0.55)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  // Bounded by the bubble (whose max-width is container-capped), NOT a fixed px width — a fixed
  // cap wider than a narrow panel forced the bubble past its container and clipped (RTL panel bug).
  maxWidth: '100%',
};

function ReplyActionButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <IconButton
      size="small"
      variant="text"
      onClick={onClick}
      aria-label={t('chat.reply.action')}
      title={t('chat.reply.action')}
    >
      <Reply size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
    </IconButton>
  );
}

// Copy the message body — an inline toolbar button beside Reply/React (Figma 14692:215825), not a menu
// item. Same ghost skin + hover as the reaction button. Carries the copy behavior the old
// MessageActionsMenu held: clipboard write + the copy_message BI beacon.
function CopyActionButton({ copySource }: { copySource: string }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  return (
    <IconButton
      size="small"
      variant="text"
      onClick={() => {
        logPageClickBi({ page_name: 'chat_home', page_version: 'v3_chat_home', button_name: 'copy_message' });
        navigator.clipboard.writeText(copySource).catch(() => {
          // Clipboard is gated by focus/permission; nothing actionable if it's denied.
        });
      }}
      aria-label={t('common.copy')}
      title={t('common.copy')}
    >
      <Copy size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
    </IconButton>
  );
}

const EMPTY_SELECTED_IDS: ReadonlySet<string> = new Set();

export function MessageRow({
  message,
  agentCardRun,
  tightenGapAfter,
  agent,
  maestroName,
  brief,
  isMaestro,
  briefParticipantIds,
  isSummaryTurn,
  isWaGroup,
  assistantName,
  participantsByWaId,
  onReply,
  onToggleReaction,
  selectedChoiceIds,
  onSelectChoice,
  onRequestConnectorLink,
  alignEdges,
}: {
  message: Message;
  agentCardRun?: NonNullable<Message['agentCard']>[];
  // This row continues a same-speaker burst — the NEXT message is another plain
  // reply from the same agent with no user turn between them — so the gap below
  // this row tightens instead of using full turn-taking spacing.
  tightenGapAfter?: boolean;
  agent?: AgentSummary;
  maestroName?: string;
  // This row is inside a brief round-table. Gates the brief-only chrome — a sub-agent's turn gets a
  // bordered bubble in its identity colour, the Maestro's turn wears the Maestro mark — matching native
  // (which draws none of this in a normal 1:1 chat even if an `agentId` is present).
  brief?: boolean;
  // This turn is the Maestro/facilitator's, even if it carries an `agentId` (the round-table's framing
  // turns do, but the id isn't a hired agent). Forces the Maestro treatment — mark + plain prose — over
  // the sub-agent bubble. Resolved by the panel against the agent roster.
  isMaestro?: boolean;
  // Distinct agent ids who spoke in this brief, first-spoke order — the MeetingSummaryCard's avatar
  // cluster + "N agents met" count.
  briefParticipantIds?: string[];
  // This row is the round-table's wrap-up — render the MeetingSummaryCard. Resolved by the panel from
  // the server flag OR the brief fetch's raw metadata (the messages API proxies to the pod in dev).
  isSummaryTurn?: boolean;
  // WhatsApp GROUP conversation: inbound member turns carry a `[Name (waId)]:`
  // attribution prefix that we decode into a sender avatar + name (display-only).
  isWaGroup?: boolean;
  assistantName?: string;
  participantsByWaId?: Map<string, string>;
  // Start a quoted reply to this message — shown as a hover action on user/assistant rows
  // (and WA-group member rows) when the surface supports composing.
  onReply?: (message: Message) => void;
  // Toggle the user's own emoji reaction on this message (quick bar + chips).
  // NOT gated on locked channels — reactions are in-app annotations.
  onToggleReaction?: (message: Message, emoji: string) => void;
  // Option ids already answered for this row's structured ask (derived from the
  // transcript's USER choiceAnswer rows) — those chips render selected.
  selectedChoiceIds?: ReadonlySet<string>;
  // Tap a choice chip → send the option's label as a normal user turn carrying
  // the structured answer. Absent (read-only surface) => inert chips.
  onSelectChoice?: (message: Message, option: ChoiceOption) => void;
  // "Get a new link" on an expired connector card → send the localized ask as a
  // normal user turn. Absent (read-only surface) => the card shows the line only.
  onRequestConnectorLink?: (message: Message, text: string) => void;
  // Full-screen desktop chat: inset the whole row (user bubble + assistant message) by
  // ROW_EDGE_INSET from the reading column's edges, so both land the same distance from it
  // (narrow/docked keep no inset — a slight zig-zag, by design there).
  alignEdges?: boolean;
}) {
  const { t } = useTranslation();
  const isUser = message.role === 'USER';
  // ASSISTANT only: an `ASSISTANT_STEP` row is transient status, not a message, so it never reaches a
  // row at all — `isVisibleMessage` filters it out of the transcript (issue #7461). Keeping it here
  // would claim a step row is a reply with a timestamp, a copy button and a reply affordance.
  const isAssistant = message.role === 'ASSISTANT';
  // Per-thread dismissal of the credits card. Local rather than persisted: the card lives in the
  // transcript, so "not now" hides THAT card rather than silencing a global prompt.
  const [creditsCardDismissed, setCreditsCardDismissed] = useState(false);
  // The quick-react emoji bar toggles open under the tool bar when the smiley is clicked
  // (Figma 14511:160345 "When Click on Emoji icon"); picking an emoji closes it.
  const [reactOpen, setReactOpen] = useState(false);
  const rawContent = message.content || '';
  const transcript = voiceTranscript(rawContent);
  const isVoice = transcript !== null;
  const content = transcript ?? rawContent;
  // A voice note carries its clip as an audio attachment — the player renders
  // ABOVE the (dimmed) transcript and replaces the gutter mic marker, which
  // stays only for player-less voice-call turns.
  const hasAudioAttachment = !!message.attachments?.some((a) => isAudioAttachment(a.mimeType));
  // Hooks must run on EVERY render, before the card/group early returns below —
  // `isWaGroup` settles async (the conversations query), so a row can re-render
  // from the plain branch into the group branch; a hook below a conditional
  // return would then change the hook count and crash the tree (React #300).
  const displayed = useTypewriter(content, isUser || !message.animate);
  // The FIRST message of a run of consecutive agentCard messages carries the
  // whole run's agents; the card self-plays once for the whole group.
  if (message.agentCard) {
    return (
      <div style={CARD_WRAP} data-msg-id={message.id}>
        <AgentJoinCard agents={agentCardRun?.length ? agentCardRun : [message.agentCard]} />
      </div>
    );
  }
  if (message.milestoneCard) {
    return (
      <div style={CARD_WRAP} data-msg-id={message.id}>
        <MilestoneCard card={message.milestoneCard} />
      </div>
    );
  }
  if (message.creditGiftCard) {
    return (
      <div style={CARD_WRAP} data-msg-id={message.id}>
        <CreditGiftCard card={message.creditGiftCard} />
      </div>
    );
  }
  // The brief round-table's wrap-up turn renders as the meeting summary card in place of a bubble
  // (matching iOS/Android). The avatar cluster + count come from the thread's participants.
  if (message.agentMeetingSummary || isSummaryTurn) {
    return (
      <div style={CARD_WRAP} data-msg-id={message.id}>
        <MeetingSummaryCard content={content} createdDate={message.createdDate} agentIds={briefParticipantIds ?? []} />
      </div>
    );
  }
  const toggleReaction = onToggleReaction ? (emoji: string) => onToggleReaction(message, emoji) : undefined;
  // WhatsApp GROUP member turn: decode the server's `[Name (waId)]:` attribution
  // into a group-chat row — sender avatar + name, body with mentions resolved,
  // the prefix hidden. Gated on `isWaGroup` so 1:1 rendering is never touched.
  const groupAttribution = isWaGroup ? parseGroupAttribution(message.content) : null;
  if (groupAttribution) {
    const time = formatMessageTime(message.createdDate);
    const resolvedText = resolveGroupMentions(groupAttribution.text, {
      assistantName: assistantName || t('chat.agentFallback'),
      participantsByWaId,
    });
    // A group member's voice note arrives voice-wrapped too — same display
    // rule: player (attachment) above, bare transcript below, dimmed.
    const groupTranscript = voiceTranscript(resolvedText);
    const text = groupTranscript ?? resolvedText;
    return (
      <div
        data-msg-id={message.id}
        className="orion-msg-row"
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginBottom: 24 }}
      >
        <div style={SPEAKER_HEADER}>
          <span aria-hidden style={{ ...SPEAKER_AVATAR, background: colorForAgent(groupAttribution.waId) }}>
            {initialsFromName(groupAttribution.displayName)}
          </span>
          <span style={SPEAKER_NAME}>{groupAttribution.displayName}</span>
        </div>
        <div style={{ display: 'flex', width: '100%', maxWidth: '100%' }}>
          <div style={USER_BUBBLE}>
            {message.replyTo && <ReplyQuote replyTo={message.replyTo} />}
            {groupTranscript !== null && message.attachments && message.attachments.length > 0 && (
              <SentAttachmentList attachments={message.attachments} tone="bubble" />
            )}
            <span
              dir="auto"
              style={{ whiteSpace: 'pre-wrap', display: 'block', ...(groupTranscript !== null ? { opacity: 0.72, marginTop: 8 } : {}) }}
            >
              <LinkifiedText text={text} />
            </span>
            {groupTranscript === null && message.attachments && message.attachments.length > 0 && (
              <SentAttachmentList attachments={message.attachments} tone="bubble" />
            )}
            {time && <div style={{ ...TIME_BASE, color: 'var(--ds-text-secondary)' }}>{time}</div>}
          </div>
        </div>
      <ReactionChips reactions={message.reactions} onToggle={toggleReaction} />
        {(toggleReaction || onReply) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {onReply && <ReplyActionButton onClick={() => onReply(message)} />}
            {toggleReaction && <ReactToggleButton open={reactOpen} onToggle={() => setReactOpen((o) => !o)} />}
          </div>
        )}
        {toggleReaction && reactOpen && (
          <div style={{ marginTop: 12 }}>
            <QuickReactBar
              onToggle={(emoji) => {
                toggleReaction(emoji);
                setReactOpen(false);
              }}
            />
          </div>
        )}
      </div>
    );
  }
  const time = formatMessageTime(message.createdDate);
  const isCursorOn = !isUser && message.animate && displayed.length < content.length;
  // Message-level copy sits in the "more" menu — suppressed while streaming and when the body has
  // code fences (they carry their own per-block copy).
  const showCopy = isAssistant && !isVoice && !isCursorOn && !content.includes('```');
  // A sub-agent's round-table turn (e.g. a brief) is attributed to that agent;
  // color is always by id, name/title from the roster. Absent => the Maestro's
  // turn, which is ALSO labeled when `maestroName` is set (brief group-chat).
  const attributedAgentId = isAssistant && !isMaestro ? message.agentId : undefined;
  const speaker = attributedAgentId
    ? { color: colorForAgent(attributedAgentId), name: agent?.name || t('chat.agentFallback'), title: agent?.title, initial: agent?.name?.[0] }
    : isAssistant && maestroName
      ? { color: 'var(--accent-solid)', name: maestroName, title: undefined, initial: agentShortForm(maestroName)[0] }
      : undefined;
  return (
    <div
      data-msg-id={message.id}
      className={isAssistant || isUser ? 'orion-msg-row' : undefined}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isUser ? 'flex-end' : 'flex-start',
        marginBottom: tightenGapAfter ? BURST_ROW_GAP : 24,
      }}
    >
      {speaker && (
        // Brief turns sit 12 below the speaker label (Figma "Morning Brief" itemSpacing 12); the
        // narrower non-brief speaker label keeps its 6.
        <div style={brief ? { ...SPEAKER_HEADER, marginBottom: 12 } : SPEAKER_HEADER}>
          {/* A sub-agent wears its bare multi-band identity dot (native's `AgentMark` — no letter
              overlaid); the Maestro wears its reserved brand mark in a brief (matching native), else
              the solid `--accent-solid` circle with its initial. */}
          {attributedAgentId ? (
            <AgentDot id={attributedAgentId} size={brief ? BRIEF_AVATAR : (SPEAKER_AVATAR.width as number)} />
          ) : brief ? (
            <MaestroMark size={BRIEF_AVATAR} />
          ) : (
            <span aria-hidden style={{ ...SPEAKER_AVATAR, background: speaker.color }}>
              {(speaker.initial ?? '·').toUpperCase()}
            </span>
          )}
          <span style={{ display: 'flex', alignItems: 'baseline', gap: 6, minWidth: 0 }}>
            <span style={SPEAKER_NAME}>{speaker.name}</span>
            {/* "Name • Role" — the bullet + role in the secondary ink (Figma "Morning Brief"). */}
            {speaker.title && (
              <span style={SPEAKER_TITLE}>
                <span aria-hidden style={{ marginInlineEnd: 4 }}>
                  •
                </span>
                {speaker.title}
              </span>
            )}
          </span>
        </div>
      )}
      <div
        style={{
          display: 'flex',
          flexDirection: isUser ? 'row-reverse' : 'row',
          alignItems: 'flex-start',
          // Figma Spacing/10 between the voice icon and the bubble (node 14511:160571).
          gap: 10,
          // Span the row so the bubble's width/max-width resolve against the full
          // row; without this the wrapper collapses and a short message wraps.
          width: '100%',
          maxWidth: '100%',
          // Full-screen desktop chat (Monday 12819056547 "[Desktop] Paddings", Figma
          // "Spacing/40"): both edges of the row carry a 40px inset from the reading
          // column, so the user bubble (flush to the row's own end) and the assistant
          // message (flush to the row's own start) land the SAME distance from the
          // column edges — that's what "aligned" means here, not zero-gap/flush.
          // Narrow/docked keep no inset (unchanged). No global border-box reset in this app, so
          // the row needs box-sizing set explicitly here — otherwise the inset padding would add
          // to the 100% width and overflow the 720px reading column by 80px.
          ...(alignEdges
            ? { boxSizing: 'border-box', paddingInlineStart: ROW_EDGE_INSET, paddingInlineEnd: ROW_EDGE_INSET }
            : {}),
        }}
      >
        {/* Voice marker: the DS mic icon button in the side gutter, centered on the first
            text line. Voice-CALL turns only — a voice NOTE's audio attachment renders
            the inline player instead. */}
        {isVoice && !hasAudioAttachment && (
          <span style={{ marginTop: isUser ? 3 : 0, display: 'inline-flex' }}>
            <MicGlyph title={isUser ? t('chat.voice.youSpoke') : t('chat.voice.saidOnCall')} />
          </span>
        )}
        <div
          style={
            isUser
              ? isVoice
                ? USER_BUBBLE_VOICE
                : USER_BUBBLE
              : brief && attributedAgentId
                ? { ...AGENT_BUBBLE, borderColor: hexToRgba(colorForAgent(attributedAgentId), 0.5) }
                : ASSISTANT_BODY
          }
        >
          {isUser && message.replyTo && <ReplyQuote replyTo={message.replyTo} />}
          {/* Voice note: player first, bare transcript below it, slightly dimmed. */}
          {isVoice && message.attachments && message.attachments.length > 0 && (
            <SentAttachmentList attachments={message.attachments} tone={isUser ? 'bubble' : 'surface'} />
          )}
          {isVoice ? (
            <span dir="auto" style={{ whiteSpace: 'pre-wrap', display: 'block', opacity: 0.72, ...(hasAudioAttachment ? { marginTop: 8 } : {}) }}>
              <LinkifiedText text={displayed} />
              {isCursorOn && <StreamingCursor />}
            </span>
          ) : isAssistant ? (
            <AssistantMarkdownContent displayed={displayed} isCursorOn={!!isCursorOn} />
          ) : (
            // display:block so dir="auto" also sets the line alignment — an inline span only
            // reorders bidi runs and leaves RTL text ragged-left inside the bubble.
            <span dir="auto" style={{ whiteSpace: 'pre-wrap', display: 'block' }}>
              {/* A picked contact's `[Name]` token renders as a bold `@Name` mention here. */}
              <ContactMentionText text={displayed} />
              {isCursorOn && <StreamingCursor />}
            </span>
          )}
          {!isVoice && message.attachments && message.attachments.length > 0 && (
            <SentAttachmentList attachments={message.attachments} tone={isUser ? 'bubble' : 'surface'} />
          )}
          {/* User keeps its timestamp inside the bubble; the assistant's moves to
              the tool bar row below (right-aligned). The voice bubble sets the mock's
              12px text→time gap and Tiny/Medium -3% tracking (Figma 14511:160571). */}
          {isUser && time && (
            <div
              style={{
                ...TIME_BASE,
                color: 'var(--ds-text-secondary)',
                ...(isVoice ? { marginTop: 12, letterSpacing: '-0.03em' } : {}),
              }}
            >
              {time}
            </div>
          )}
        </div>
      </div>
      {/* The opportunity suggestion — chip, win, how. It must sit ABOVE the choice chips: those two
          chips ARE its CTAs, so the card carries no buttons of its own and reads as their subject. */}
      {isAssistant && message.opportunityCard && <OpportunityCard card={message.opportunityCard} />}
      {/* "Running low on credits" card — rides the reply like the connector card, so the assistant's
          answer is never replaced by the ask. Dismissing hides it for this thread only. */}
      {isAssistant && message.creditsCard && !creditsCardDismissed && (
        <CreditsCard card={message.creditsCard} onDismiss={() => setCreditsCardDismissed(true)} />
      )}
      {isAssistant && message.choices && (
        <ChoiceChips
          choices={message.choices}
          selectedIds={selectedChoiceIds ?? EMPTY_SELECTED_IDS}
          onSelect={onSelectChoice ? (option) => onSelectChoice(message, option) : undefined}
        />
      )}
      {/* Connector connect card — the reply text carries no URL, this card IS the connect
          affordance (arrives in-band at messageEnd, like choices). */}
      {isAssistant && message.connectorCard && (
        <ConnectorConnectCard
          card={message.connectorCard}
          messageId={message.finalId ?? message.id}
          onRequestNewLink={onRequestConnectorLink ? (text) => onRequestConnectorLink(message, text) : undefined}
        />
      )}
      <ReactionChips reactions={message.reactions} onToggle={toggleReaction} />
      {/* A brief turn keeps the clean round-table look (Figma "Morning Brief"): just the timestamp,
          left-aligned directly below the message — no copy/reply/react tool bar. */}
      {isAssistant && brief
        ? time && (
            // 12 below the bubble, matching the 12 above it (Figma "Morning Brief" itemSpacing 12).
            <div style={{ ...TIME_BASE, marginTop: 12, textAlign: 'start', color: 'var(--ds-text-secondary)' }}>{time}</div>
          )
        : /* Assistant tool bar: inline reply · copy · react buttons on the left (gap 4), timestamp on
             the right, 12px below the message — always visible, as the mock shows it (a user's own
             message has no tool bar). Reply and Copy are their own icon buttons, not a "more" menu
             (Figma 14692:215825). Clicking react opens the quick-react emoji bar as a second row below. */
          isAssistant &&
          (time || showCopy || onReply || toggleReaction) && (
            <>
              <div
                style={
                  alignEdges
                    ? {
                        ...TOOLBAR_ROW,
                        boxSizing: 'border-box',
                        paddingInlineStart: ROW_EDGE_INSET,
                        paddingInlineEnd: ROW_EDGE_INSET,
                      }
                    : TOOLBAR_ROW
                }
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {onReply && <ReplyActionButton onClick={() => onReply(message)} />}
                  {showCopy && <CopyActionButton copySource={content} />}
                  {toggleReaction && <ReactToggleButton open={reactOpen} onToggle={() => setReactOpen((o) => !o)} />}
                </div>
                {time && <div style={{ ...TIME_BASE, marginTop: 0, color: 'var(--ds-text-secondary)' }}>{time}</div>}
              </div>
              {toggleReaction && reactOpen && (
                <div style={{ marginTop: 12 }}>
                  <QuickReactBar
                    onToggle={(emoji) => {
                      toggleReaction(emoji);
                      setReactOpen(false);
                    }}
                  />
                </div>
              )}
            </>
          )}
    </div>
  );
}

// Brief speaker avatar — 28px (Figma "Morning Brief"), a touch larger than the 24px WA-group avatar.
const BRIEF_AVATAR = 28;

// Full-screen desktop chat's inline inset off the reading column edges (Figma "Spacing/40",
// Monday 12819056547 "[Desktop] Paddings" — a follow-up correction to the original alignEdges
// pass, which had dropped the assistant body's trailing inset to 0 instead of this value).
const ROW_EDGE_INSET = 40;

// Gap after a message that continues a same-speaker burst (see `continuesAssistantRun`) — tight
// enough that consecutive bubbles from one agent read as one thought, not a repeat of the full
// 24px turn-taking gap for every bubble in the run.
const BURST_ROW_GAP = 6;

const TOOLBAR_ROW: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  // Figma "Full screen / Chat Layout" QA (Monday 12819150619): 12px between the answer and its
  // reply/copy/react toolbar — was 8.
  marginTop: 12,
  width: '100%',
  maxWidth: 720,
};
